import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  TextField,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useAuth } from "../../../../context/AuthContext";
import StoreProductsTable from "./StoreProductsTable";
import StoreProductDeleteModal from "./StoreProductDeleteModal";
import ProductEditorPanel from "./ProductEditorPanel";

export default function StoreProductsPanel() {
  const { authFetch, user } = useAuth();

  const [showEditor, setShowEditor] = useState(false);
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [banner, setBanner] = useState({
    open: false,
    type: "success",
    message: "",
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const canManage = user?.role === "admin";

  const loadProducts = async () => {
    setLoading(true);

    try {
      const response = await authFetch("/api/store/products/", {
        method: "GET",
      });

      const data = await response.json().catch(() => []);

      if (!response.ok) {
        setBanner({
          open: true,
          type: "error",
          message: data?.error || "No se pudieron cargar los productos",
        });
        setProducts([]);
        return;
      }

      setProducts(Array.isArray(data) ? data : []);
    } catch {
      setBanner({
        open: true,
        type: "error",
        message: "Ocurrió un error al cargar los productos",
      });
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showEditor) {
      loadProducts();
    }
  }, [showEditor]);

  const filteredProducts = useMemo(() => {
    const term = query.trim().toLowerCase();

    if (!term) return products;

    return products.filter((item) => {
      const name = String(item.name || "").toLowerCase();
      const code = String(item.product_code || "").toLowerCase();
      const brand = String(item.brand_name || "").toLowerCase();
      const category = String(item.category_name || "").toLowerCase();
      const section = String(item.section_name || "").toLowerCase();

      return (
        name.includes(term) ||
        code.includes(term) ||
        brand.includes(term) ||
        category.includes(term) ||
        section.includes(term)
      );
    });
  }, [products, query]);

  const handleCreate = () => {
    setSelectedProduct(null);
    setShowEditor(true);
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setShowEditor(true);
  };

  const handleDeleteOpen = (product) => {
    setDeleteTarget(product);
  };

  const handleDeleteClose = () => {
    if (actionLoadingId.startsWith("delete-")) return;
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setActionLoadingId(`delete-${deleteTarget.uuid}`);

    try {
      const response = await authFetch(
        `/api/store/products/${deleteTarget.uuid}/delete/`,
        {
          method: "DELETE",
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        setBanner({
          open: true,
          type: "error",
          message: data?.error || "No se pudo eliminar el producto",
        });
        return;
      }

      setProducts((prev) => prev.filter((item) => item.uuid !== deleteTarget.uuid));
      setBanner({
        open: true,
        type: "success",
        message: data?.message || "Producto eliminado correctamente",
      });
      setDeleteTarget(null);
    } catch {
      setBanner({
        open: true,
        type: "error",
        message: "Ocurrió un error al eliminar el producto",
      });
    } finally {
      setActionLoadingId("");
    }
  };

  if (showEditor && canManage) {
    return (
      <Box sx={{ width: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.8 }}
        >
          <Box />

          <Button
            onClick={() => {
              setShowEditor(false);
              setSelectedProduct(null);
            }}
            variant="outlined"
            startIcon={<ArrowBackRoundedIcon />}
            sx={{
              height: 36,
              px: 1.4,
              borderRadius: "9px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#334155",
              borderColor: "rgba(51,65,85,0.18)",
              "&:hover": {
                borderColor: "rgba(51,65,85,0.28)",
                bgcolor: "rgba(148,163,184,0.08)",
              },
            }}
          >
            Regresar
          </Button>
        </Stack>

        <ProductEditorPanel
          product={selectedProduct}
          onSuccess={(message) => {
            setBanner({
              open: true,
              type: "success",
              message,
            });
            setShowEditor(false);
            setSelectedProduct(null);
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={1.4}
        sx={{ mb: 1.8 }}
      >
        <Stack direction="row" spacing={1}>
          <TextField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar producto"
            size="small"
            InputProps={{
              startAdornment: (
                <SearchRoundedIcon sx={{ fontSize: 18, color: "#64748b", mr: 0.8 }} />
              ),
            }}
            sx={{
              minWidth: { xs: "100%", sm: 400 },
              "& .MuiOutlinedInput-root": {
                height: 38,
                borderRadius: "10px",
                bgcolor: "#ffffff",
              },
            }}
          />

          <Button
            onClick={loadProducts}
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            sx={{
              height: 38,
              px: 1.4,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#334155",
              borderColor: "rgba(51,65,85,0.18)",
              "&:hover": {
                borderColor: "rgba(51,65,85,0.28)",
                bgcolor: "rgba(148,163,184,0.08)",
              },
            }}
          >
            Recargar
          </Button>
        </Stack>

        {canManage && (
          <Button
            onClick={handleCreate}
            variant="contained"
            startIcon={<AddRoundedIcon />}
            sx={{
              height: 38,
              px: 1.4,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#ffffff",
              bgcolor: "#0f172a",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#111827",
                boxShadow: "none",
              },
            }}
          >
            Nuevo producto
          </Button>
        )}
      </Stack>

      {loading ? (
        <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 5 }}>
          <CircularProgress size={28} />
        </Stack>
      ) : (
        <StoreProductsTable
          products={filteredProducts}
          onEdit={handleEdit}
          onDelete={handleDeleteOpen}
          canManage={canManage}
          actionLoadingId={actionLoadingId}
        />
      )}

      <StoreProductDeleteModal
        open={Boolean(deleteTarget)}
        onClose={handleDeleteClose}
        onConfirm={handleDelete}
        product={deleteTarget}
        loading={actionLoadingId.startsWith("delete-")}
      />

      <Snackbar
        open={banner.open}
        autoHideDuration={3500}
        onClose={() => setBanner((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={banner.type}
          variant="filled"
          sx={{
            borderRadius: "12px",
            bgcolor:
              banner.type === "error"
                ? "rgba(220,38,38,0.12)"
                : "rgba(34,197,94,0.12)",
            color:
              banner.type === "error"
                ? "#dc2626"
                : "#166534",
            fontWeight: 600,
            "& .MuiAlert-icon": {
              color:
                banner.type === "error"
                  ? "#dc2626"
                  : "#166534",
            },
          }}
        >
          {banner.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}